import { useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { subscribersPath, rootPath } from '../../../logic/paths'
import { hasValidAccessToken } from '../../../logic/apis/auth'
import useIsSuperAdmin from '../../hooks/useIsSuperAdmin'
import { notify } from '../util'

// NOTE: hasValidAccessToken() reads the in-memory access token (post-Wave 3C).
// App.tsx awaits bootstrapSession() before rendering these guards, so on
// a reload with a valid httpOnly refresh cookie the in-memory token has
// already been populated by the time these components first render.

const AuthRedirect = ({ children }) => {
  // If an unexpired token exists, send the user to the authed area;
  // otherwise render the login/unauthed children.
  return hasValidAccessToken() ? <Navigate to={subscribersPath} /> : children
}

export const AuthRedirect1 = ({ children }) => {
  // Guard for authed routes — only allow through with a valid, unexpired token.
  return hasValidAccessToken() ? children : <Navigate to={rootPath} />
}

/**
 * Role guard for routes whose ENTIRE contents are SuperAdmin-only server-side.
 *
 * Hiding the sidebar entry is not a guard: `AuthRedirect1` admits any valid
 * session, so a plain Admin who types or bookmarks the path still lands on a
 * page where every request 400s. Bounce them instead, to the same place
 * `AuthRedirect` sends an already-signed-in admin, and say why once — a silent
 * redirect from a bookmark reads as a broken link.
 *
 * `authSlice` seeds the role from localStorage at store-creation time, so it is
 * already correct on the first render after a reload; there is no unauthorized
 * flash and no window where a real Super Admin gets bounced.
 */
export const RequireSuperAdmin = ({ children }: { children: JSX.Element }) => {
  const isSuperAdmin = useIsSuperAdmin()
  // Fires once per refusal. Without the ref this effect double-invokes under
  // React 18 StrictMode and the visitor gets TWO identical toasts — confirmed
  // on the deployed dev panel, not just in local dev: a single navigation to
  // /admin as a plain Admin produced two. `notify` is a side effect on a
  // component that also returns <Navigate>, so it needs its own guard rather
  // than relying on the effect running once.
  const warned = useRef(false)

  useEffect(() => {
    if (isSuperAdmin) {
      warned.current = false
      return
    }
    if (warned.current) return
    warned.current = true
    notify('You need Super Admin access to open that page', 'error')
  }, [isSuperAdmin])

  return isSuperAdmin ? children : <Navigate to={subscribersPath} replace />
}

export default AuthRedirect
